import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import CustomTable from '../../containers/Table'
import { useContext, useEffect, useState } from 'react'
import { UserDTO } from '../../models/users'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import CustomIconButton from '../../components/IconButton'
import AdminService from '../../services/AdminService'
import { CircularProgress, Tooltip } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import CustomModal from '../../containers/Modal'
import ConfirmationModal from '../../components/Modal/ConfirmationModal'
import { ROOT_ADMIN_EMAIL } from '../../helpers/utils'
import { PageContext } from '../../context/PageContext'

const AdminsTable = () => {
    const [adminToDelete, setAdminToDelete] = useState<string>('')
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
    const [admins, setAdmins] = useState<Array<UserDTO>>()
    const { enqueueSnackbar } = useSnackbar()
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate()
    const { smallScreen } = useContext(PageContext)

    useEffect(() => {
        new AdminService().getAll().then((response) => {
            if (response.status !== 200) return
            setAdmins(response.data)
        })
    }, [])

    const handleRemoveAdmin = async (id: string) => {
        const response = await new AdminService().delete(id)
        if (response.status !== 200) {
            enqueueSnackbar('Error en la solicitud.', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        } else {
            enqueueSnackbar('Administrador eliminado correctamente.', {
                variant: 'success',
                autoHideDuration: 6000,
            })
        }
        setIsDeleteModalOpen(false)
        setTimeout(() => navigate(0), 1500)
    }

    const columnHelper = createColumnHelper<UserDTO>()
    const columns: ColumnDef<UserDTO, any>[] = [
        columnHelper.accessor('email', {
            header: 'Email',
            cell: ({ getValue }) => (
                <div className="text-sm text-secondary w-[200px]">{getValue<string>()}</div>
            ),
        }),
    ]

    if (!smallScreen) {
        columns.push(
            columnHelper.accessor('firstName', {
                header: 'Nombre',
                cell: ({ getValue }) => (
                    <div className="text-sm text-secondary w-[200px]">{getValue<string>()}</div>
                ),
            }),
            columnHelper.accessor('lastName', {
                header: 'Apellido',
                cell: ({ getValue }) => (
                    <div className="text-sm text-secondary w-[200px]">{getValue<string>()}</div>
                ),
            }),
            columnHelper.accessor('id', {
                header: '',
                cell: ({ row }) => (
                    <div className="w-4">
                        {row.original.email !== ROOT_ADMIN_EMAIL ? (
                            <CustomIconButton
                                icon={<DeleteForeverIcon />}
                                onClick={() => {
                                    setAdminToDelete(row.original.id)
                                    setIsDeleteModalOpen(true)
                                }}
                            />
                        ) : (
                            <Tooltip
                                title="Administrador principal"
                                placement="left"
                            >
                                <div>
                                    <CustomIconButton icon={<VerifiedUserIcon />} />
                                </div>
                            </Tooltip>
                        )}
                    </div>
                ),
            }),
        )
    } else {
        columns.push(
            columnHelper.accessor('id', {
                header: '',
                cell: ({ row }) => (
                    <div className="w-4">
                        {row.original.email !== ROOT_ADMIN_EMAIL ? (
                            <CustomIconButton
                                icon={<DeleteForeverIcon />}
                                onClick={() => {
                                    setAdminToDelete(row.original.id)
                                    setIsDeleteModalOpen(true)
                                }}
                            />
                        ) : (
                            <Tooltip
                                title="Administrador principal"
                                placement="left"
                            >
                                <div>
                                    <CustomIconButton icon={<VerifiedUserIcon />} />
                                </div>
                            </Tooltip>
                        )}
                    </div>
                ),
            }),
        )
    }

    return (
        <div>
            {admins ? (
                <CustomTable
                    columns={columns}
                    data={admins}
                    pageSize={10}
                    withPagination
                />
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            )}
            <CustomModal
                isOpen={isDeleteModalOpen}
                handleClose={() => setIsDeleteModalOpen(false)}
            >
                <ConfirmationModal
                    title="¿Estás seguro de que querés eliminar a este administrador?"
                    subtitle="Esta acción es irreversible"
                    confirm={() => {
                        setLoading(true)
                        handleRemoveAdmin(adminToDelete)
                    }}
                    close={() => setIsDeleteModalOpen(false)}
                    loading={loading}
                />
            </CustomModal>
        </div>
    )
}

export default AdminsTable
